import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  apiClient as defaultClient,
  type ApiClient,
  type CapabilityRunView,
  type DetectedStep,
  type JourneyModelingOutcome,
  type JourneyModelingStatusView,
  type MarkedRevisionView,
  type ModelRevisionSummary,
} from '../api/client';
import { AppShell } from '../components/AppShell';
import { Card } from '../components/Card';
import { Spinner } from '../components/Spinner';
import { Button } from '../components/Button';
import { InlineAlert } from '../components/InlineAlert';
import type { AlertSeverity } from '../components/InlineAlert';
import { StatusBadge } from '../components/StatusBadge';
import { useOrg } from '../org/context';
import { useFocusMain } from '../a11y/useFocusMain';
import { toDisplayError } from '../api/errorMessages';
import { isApiError } from '../api/errors';
import { verdictReason, VERDICT_SUMMARY } from '../capability/capabilityCopy';
import { formatMicroUsd } from '../capability/capabilityCopy';
import { AuthedBrand } from './AuthedBrand';
import { journeyTypeLabel } from './journeyLabels';
import { RunModelingDialog } from './RunModelingDialog';
import styles from './JourneyDetail.module.css';

type LoadState = 'loading' | 'error' | 'unavailable' | 'ready';
type SectionState = 'idle' | 'loading' | 'error' | 'ready';
type RunState = 'idle' | 'checking-key' | 'confirming' | 'running' | 'done' | 'failed';

export interface JourneyDetailProps {
  client?: ApiClient;
}

function formatTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

/** Honest cost label (#34-D3): a formatted figure, or "unavailable" when the
 * amount is null/malformed — NEVER $0 for an absent value. A genuine priced $0
 * run truthfully renders as ~$0. */
function costLabel(actualMicroUsd: string | null): string {
  return formatMicroUsd(actualMicroUsd) ?? 'unavailable';
}

/**
 * Journey detail + AI model-revision view (#228 / Story 34-D1). A READ-ONLY
 * surface anchored on a journey UUID (the latest-materialized row for its type,
 * resolved from journey-health). It renders TWO distinct datasets with their own
 * empty/error states: the deterministic detected steps (#129, no provider call)
 * and the AI model revisions. It is HONEST-BY-DEFAULT — every modeled step/result
 * carries its 34-C markers (tentative / appliedThreshold / failClosed) and every
 * expected result is shown as "Unverified"; nothing is ever presented as verified.
 * No key, no provider call, no cost in this slice. One-shot fetch (no polling).
 */
export function JourneyDetail({ client = defaultClient }: JourneyDetailProps) {
  const { currentOrg } = useOrg();
  const { id, journeyId, revisionId } = useParams<{
    id: string;
    journeyId: string;
    revisionId: string;
  }>();
  useFocusMain();

  const orgId = currentOrg?.id;

  const [phase, setPhase] = useState<LoadState>('loading');
  const [loadMessage, setLoadMessage] = useState('');
  const [journeyType, setJourneyType] = useState<string | null>(null);

  const [steps, setSteps] = useState<DetectedStep[] | null>(null);
  const [stepsError, setStepsError] = useState<string | null>(null);

  const [revisions, setRevisions] = useState<ModelRevisionSummary[] | null>(null);
  const [revisionsError, setRevisionsError] = useState<string | null>(null);

  const [revision, setRevision] = useState<MarkedRevisionView | null>(null);
  const [revisionPhase, setRevisionPhase] = useState<SectionState>('idle');
  const [revisionError, setRevisionError] = useState<string | null>(null);

  // Org-wide BYOK capability of the configured model (#31-B read), loaded
  // independently — its loading/error states are DISTINCT and never default to
  // "verified".
  const [capPhase, setCapPhase] = useState<SectionState>('loading');
  const [capRun, setCapRun] = useState<CapabilityRunView | null>(null);
  // AI-enrichment pending state after a key/quota modeling failure (#33, FR-11.9).
  const [modelingStatus, setModelingStatus] = useState<JourneyModelingStatusView | null>(null);

  // Modeling-run interaction (#229), layered on the load machine above.
  const [runState, setRunState] = useState<RunState>('idle');
  const [runOutcome, setRunOutcome] = useState<JourneyModelingOutcome | null>(null);
  const [runMessage, setRunMessage] = useState<string | null>(null);
  const [runNeedsKey, setRunNeedsKey] = useState(false);
  const [runRaced, setRunRaced] = useState(false);
  const [keyModel, setKeyModel] = useState<string | null>(null);
  const runInFlight = useRef(false);

  // #422 — generate the deterministic Gherkin test case for THIS journey
  // (project:write). Works for discovered and customer-provided journeys alike.
  const [generating, setGenerating] = useState(false);
  const [generateMessage, setGenerateMessage] = useState<string | null>(null);

  // #423 — run THIS journey (customer-provided only; a discovered journey has no
  // attested steps, so the server rejects it). Separate from the modeling machine.
  const [source, setSource] = useState<string | null>(null);
  const [journeyRunning, setJourneyRunning] = useState(false);
  const [journeyRunMessage, setJourneyRunMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !id || !journeyId) return;
    setPhase('loading');
    try {
      // Resolve the UUID against journey-health to get the type (for the detected
      // steps, which are type-keyed) and to prove the journey exists. An id that
      // resolves to no health entry is UNAVAILABLE — never substituted or treated
      // as empty.
      const health = await client.journeyHealth(orgId, id);
      const entry = health.journeys.find((j) => j.journeyId === journeyId) ?? null;
      if (!entry) {
        setPhase('unavailable');
        return;
      }
      setJourneyType(entry.journeyType);
      setSource(entry.source);

      const [revResult, stepsResult] = await Promise.allSettled([
        client.getJourneyModelRevisions(orgId, id, journeyId),
        client.journeyDetectedSteps(orgId, id, entry.journeyType),
      ]);

      if (revResult.status === 'fulfilled') {
        setRevisions(revResult.value);
        setRevisionsError(null);
      } else {
        setRevisions(null);
        setRevisionsError(toDisplayError(revResult.reason).message);
      }

      if (stepsResult.status === 'fulfilled') {
        setSteps(stepsResult.value.steps);
        setStepsError(null);
      } else {
        setSteps(null);
        setStepsError(toDisplayError(stepsResult.reason).message);
      }

      setPhase('ready');
    } catch (err) {
      setLoadMessage(toDisplayError(err).message);
      setPhase('error');
    }
  }, [client, orgId, id, journeyId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!orgId || !id || !journeyId || !revisionId) {
      setRevision(null);
      setRevisionError(null);
      setRevisionPhase('idle');
      return;
    }
    let active = true;
    setRevisionPhase('loading');
    setRevisionError(null);
    client
      .getJourneyModelRevision(orgId, id, journeyId, revisionId)
      .then((r) => {
        if (!active) return;
        setRevision(r);
        setRevisionPhase('ready');
      })
      .catch((err) => {
        if (!active) return;
        setRevisionError(toDisplayError(err).message);
        setRevisionPhase('error');
      });
    return () => {
      active = false;
    };
  }, [client, orgId, id, journeyId, revisionId]);

  // Capability read is org-wide and independent of the per-journey load.
  useEffect(() => {
    if (!orgId) return;
    let active = true;
    setCapPhase('loading');
    client
      .getLatestCapabilityRun(orgId)
      .then((run) => {
        if (!active) return;
        setCapRun(run);
        setCapPhase('ready');
      })
      .catch(() => {
        if (!active) return;
        setCapRun(null);
        setCapPhase('error');
      });
    return () => {
      active = false;
    };
  }, [client, orgId]);

  const refreshRevisions = useCallback(async () => {
    if (!orgId || !id || !journeyId) return;
    try {
      const next = await client.getJourneyModelRevisions(orgId, id, journeyId);
      setRevisions(next);
      setRevisionsError(null);
    } catch (err) {
      setRevisionsError(toDisplayError(err).message);
    }
  }, [client, orgId, id, journeyId]);

  // Point-of-use key check: only read the key when the user asks to run. A missing
  // or revoked key means NO POST (no provider call, no spend) — the user is sent to
  // reconfigure it.
  const onRunClick = useCallback(async () => {
    if (!orgId || runInFlight.current || runState === 'checking-key' || runState === 'running') {
      return;
    }
    setRunState('checking-key');
    setRunOutcome(null);
    setRunMessage(null);
    setRunNeedsKey(false);
    setRunRaced(false);
    try {
      const key = await client.getLlmKey(orgId);
      if (!key.configured || key.status === 'revoked') {
        setRunNeedsKey(true);
        setRunState('failed');
        return;
      }
      setKeyModel(key.model);
      setRunState('confirming');
    } catch (err) {
      setRunMessage(toDisplayError(err).message);
      setRunState('failed');
    }
  }, [client, orgId, runState]);

  const refreshModelingStatus = useCallback(async () => {
    if (!orgId || !id || !journeyId) return;
    try {
      setModelingStatus(await client.getJourneyModelingStatus(orgId, id, journeyId));
    } catch {
      setModelingStatus(null);
    }
  }, [client, orgId, id, journeyId]);

  useEffect(() => {
    void refreshModelingStatus();
  }, [refreshModelingStatus]);

  const onConfirmRun = useCallback(async () => {
    if (!orgId || !id || !journeyId || runInFlight.current) return;
    runInFlight.current = true;
    setRunState('running');
    try {
      const outcome = await client.postJourneyModel(orgId, id, journeyId);
      setRunOutcome(outcome);
      setRunState('done');
      if (outcome.status === 'generated') {
        await refreshRevisions();
      }
    } catch (err) {
      // A 404 here is a RACE: the key was active a moment ago (we just checked),
      // so it was revoked mid-flight OR the journey was removed since page load.
      // Both are `not_found`; we can't tell them apart, so we say so honestly
      // rather than asserting the key is the cause.
      if (isApiError(err) && err.code === 'not_found') {
        setRunRaced(true);
      } else {
        setRunMessage(toDisplayError(err).message);
      }
      setRunState('failed');
    } finally {
      runInFlight.current = false;
      // Reflect any key/quota pending state the run just set or cleared (#33).
      void refreshModelingStatus();
    }
  }, [client, orgId, id, journeyId, refreshRevisions, refreshModelingStatus]);

  const onCancelRun = useCallback(() => {
    if (runInFlight.current) return;
    setRunState('idle');
  }, []);

  const onGenerate = useCallback(async () => {
    if (!orgId || !id || !journeyId || generating) return;
    setGenerating(true);
    setGenerateMessage(null);
    try {
      const { created } = await client.generateTestCase(orgId, id, journeyId);
      setGenerateMessage(
        created
          ? 'Test case generated. Open Test cases to view, edit, or export it.'
          : 'Test case is already up to date. Open Test cases to view it.',
      );
    } catch (err) {
      setGenerateMessage(toDisplayError(err).message);
    } finally {
      setGenerating(false);
    }
  }, [client, orgId, id, journeyId, generating]);

  const onRunJourney = useCallback(async () => {
    if (!orgId || !id || !journeyId || journeyRunning) return;
    setJourneyRunning(true);
    setJourneyRunMessage(null);
    try {
      const result = await client.triggerJourneyRun(orgId, id, {
        selectionMode: 'single',
        journeyIds: [journeyId],
      });
      const n = result.runIds.length;
      setJourneyRunMessage(
        `Started ${n} run${n === 1 ? '' : 's'}. The outcome and any potential defect appear once the run finishes.`,
      );
    } catch (err) {
      setJourneyRunMessage(toDisplayError(err).message);
    } finally {
      setJourneyRunning(false);
    }
  }, [client, orgId, id, journeyId, journeyRunning]);

  const label = journeyType ? journeyTypeLabel(journeyType) : 'Journey';
  const canWrite = currentOrg?.role === 'org_admin' || currentOrg?.role === 'editor';
  const backHref = `/projects/${id ?? ''}/journey-health`;
  const basePath = `/projects/${id ?? ''}/journeys/${journeyId ?? ''}`;
  const keySettingsHref = '/settings/llm-key';
  const runBusy = runState === 'checking-key' || runState === 'running';

  return (
    <AppShell brand={<AuthedBrand />}>
      <Card>
        <p className={styles.back}>
          <Link to={backHref}>Back to journey health</Link>
        </p>

        <h1 id="jd-heading" className={styles.heading}>
          {label} — journey model
        </h1>
        <p className={styles.subheading}>
          The steps TestCraft detected for this journey and any AI-modeled revisions of it. Modeled
          revisions are drafted by an AI from what was observed &mdash; they are shown unverified and
          are never run or confirmed here.
        </p>

        {phase === 'loading' ? (
          <div className={styles.loading} role="status">
            <Spinner />
            <span>Loading journey detail…</span>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div className={styles.loadError}>
            <InlineAlert severity="error">{loadMessage}</InlineAlert>
            <Button variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        ) : null}

        {phase === 'unavailable' ? (
          <div className={styles.panel}>
            <InlineAlert severity="warning">
              This journey is no longer available. It may have aged out of your retention window or
              been superseded by a newer scan.{' '}
              <Link to={backHref}>Back to journey health</Link>
            </InlineAlert>
          </div>
        ) : null}

        {phase === 'ready' ? (
          <>
            {canWrite ? (
              <section className={styles.section} aria-label="Test case">
                <div className={styles.pendingActions}>
                  <Button
                    variant="secondary"
                    busy={generating}
                    onClick={() => void onGenerate()}
                    data-testid="generate-test-case"
                  >
                    {generating ? 'Generating…' : 'Generate test case'}
                  </Button>
                  <Link to={`/projects/${id ?? ''}/test-cases`}>View test cases</Link>
                </div>
                {generateMessage ? (
                  <InlineAlert severity="info">{generateMessage}</InlineAlert>
                ) : null}
              </section>
            ) : null}
            {canWrite && source === 'customer-provided' ? (
              <section className={styles.section} aria-label="Run this journey">
                <div className={styles.pendingActions}>
                  <Button
                    variant="primary"
                    busy={journeyRunning}
                    onClick={() => void onRunJourney()}
                    data-testid="run-journey"
                  >
                    {journeyRunning ? 'Starting run…' : 'Run this journey'}
                  </Button>
                </div>
                {journeyRunMessage ? (
                  <InlineAlert severity="info">{journeyRunMessage}</InlineAlert>
                ) : null}
              </section>
            ) : null}
            <DetectedStepsSection steps={steps} error={stepsError} />
            {modelingStatus?.pending ? (
              <section className={styles.section} aria-label="AI enrichment status">
                <InlineAlert severity="warning">
                  {modelingStatus.state === 'pending_quota'
                    ? 'AI modeling is paused — the AI provider rate-limited or exhausted the quota for your key. '
                    : 'AI modeling is paused — the AI provider rejected your key. '}
                  Restore your key or quota, then resume.{' '}
                  <Link to={keySettingsHref}>Model &amp; key settings</Link>
                </InlineAlert>
                <div className={styles.pendingActions}>
                  <Button variant="primary" busy={runBusy} onClick={() => void onRunClick()}>
                    Resume AI modeling
                  </Button>
                </div>
              </section>
            ) : null}
            <RevisionsSection
              revisions={revisions}
              error={revisionsError}
              basePath={basePath}
              activeRevisionId={revisionId ?? null}
              runButton={
                <Button
                  variant="primary"
                  busy={runBusy}
                  onClick={() => void onRunClick()}
                  data-testid="run-modeling"
                >
                  Run AI modeling
                </Button>
              }
              banner={<CapabilityBanner phase={capPhase} run={capRun} keySettingsHref={keySettingsHref} />}
              outcome={
                <RunOutcome
                  runState={runState}
                  outcome={runOutcome}
                  message={runMessage}
                  needsKey={runNeedsKey}
                  raced={runRaced}
                  keySettingsHref={keySettingsHref}
                  model={keyModel}
                  capRun={capRun}
                />
              }
            />
            {revisionId ? (
              <RevisionDetailSection
                phase={revisionPhase}
                revision={revision}
                error={revisionError}
              />
            ) : null}
          </>
        ) : null}
      </Card>

      {runState === 'confirming' || runState === 'running' ? (
        <RunModelingDialog
          model={keyModel}
          busy={runState === 'running'}
          onConfirm={() => void onConfirmRun()}
          onCancel={onCancelRun}
        />
      ) : null}
    </AppShell>
  );
}

function DetectedStepsSection({
  steps,
  error,
}: {
  steps: DetectedStep[] | null;
  error: string | null;
}) {
  return (
    <section className={styles.section} aria-labelledby="jd-detected-heading">
      <h2 id="jd-detected-heading" className={styles.sectionHeading}>
        Detected steps
      </h2>
      <p className={styles.sectionHelp}>
        The deterministic path TestCraft pieced together from the crawl &mdash; no AI, no key, no
        cost.
      </p>
      {error ? (
        <InlineAlert severity="error">{error}</InlineAlert>
      ) : steps && steps.length > 0 ? (
        <ol className={styles.stepList}>
          {steps.map((step) => (
            <li key={step.stepOrder} className={styles.step}>
              <span className={styles.stepOrder}>{step.stepOrder}</span>
              <span className={styles.stepLabel}>{step.description}</span>
              {step.orderingInferred ? <span className={styles.mutedBadge}>inferred</span> : null}
              {step.tentative ? <span className={styles.mutedBadge}>tentative</span> : null}
              <span className={styles.stepMeta}>{Math.round(step.confidence * 100)}% confidence</span>
            </li>
          ))}
        </ol>
      ) : (
        <InlineAlert severity="info">No detected steps for this journey yet.</InlineAlert>
      )}
    </section>
  );
}

function RevisionsSection({
  revisions,
  error,
  basePath,
  activeRevisionId,
  runButton,
  banner,
  outcome,
}: {
  revisions: ModelRevisionSummary[] | null;
  error: string | null;
  basePath: string;
  activeRevisionId: string | null;
  runButton: ReactNode;
  banner: ReactNode;
  outcome: ReactNode;
}) {
  return (
    <section className={styles.section} aria-labelledby="jd-revisions-heading">
      <div className={styles.sectionHeaderRow}>
        <h2 id="jd-revisions-heading" className={styles.sectionHeading}>
          AI model revisions
        </h2>
        {runButton}
      </div>
      <p className={styles.sectionHelp}>
        Each revision is an AI-drafted model of this journey. Revisions are unverified drafts &mdash;
        TestCraft never runs or confirms them here.
      </p>
      {banner}
      {outcome}
      {error ? (
        <InlineAlert severity="error">{error}</InlineAlert>
      ) : revisions && revisions.length > 0 ? (
        <ul className={styles.revisionList}>
          {revisions.map((rev) => (
            <li
              key={rev.revisionId}
              className={rev.revisionId === activeRevisionId ? styles.revisionActive : styles.revisionItem}
            >
              <Link to={`${basePath}/revisions/${rev.revisionId}`} className={styles.revisionLink}>
                Revision {rev.revisionNumber}
              </Link>
              <span className={styles.revisionMeta}>
                {rev.model} · {rev.stepCount} step{rev.stepCount === 1 ? '' : 's'} ·{' '}
                {rev.generatorVersion}
                {formatTime(rev.createdAt) ? ` · ${formatTime(rev.createdAt)}` : ''}
              </span>
              <span className={styles.revisionCost}>Cost: {costLabel(rev.actualMicroUsd)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <InlineAlert severity="info">
          No AI model revisions yet. This journey has only its detected steps so far.
        </InlineAlert>
      )}
    </section>
  );
}

/**
 * Org-wide BYOK model-capability banner (#31-C fold). Capability WARNS, it never
 * blocks: the Run action stays enabled for every state. A capability read-error or
 * loading state is DISTINCT and never shown as "verified". Only a `done` +
 * `verified` run shows the Verified badge, scoped explicitly to model capability.
 */
function CapabilityBanner({
  phase,
  run,
  keySettingsHref,
}: {
  phase: SectionState;
  run: CapabilityRunView | null;
  keySettingsHref: string;
}) {
  if (phase === 'loading') {
    return (
      <p className={styles.capabilityNote}>Checking your model’s capability…</p>
    );
  }
  if (phase === 'error') {
    return (
      <div className={styles.capabilityBanner}>
        <InlineAlert severity="warning">
          We couldn’t check your model’s capability right now. You can still run modeling.{' '}
          <Link to={keySettingsHref}>Model &amp; key settings</Link>
        </InlineAlert>
      </div>
    );
  }
  // ready
  if (!run) {
    return (
      <div className={styles.capabilityBanner}>
        <InlineAlert severity="info">
          Your model’s capability hasn’t been checked yet.{' '}
          <Link to={keySettingsHref}>Check model capability</Link>
        </InlineAlert>
      </div>
    );
  }
  if (run.state === 'queued' || run.state === 'running') {
    return (
      <p className={styles.capabilityNote}>A capability check is in progress for your model.</p>
    );
  }
  if (run.state === 'failed' || run.status === null) {
    return (
      <div className={styles.capabilityBanner}>
        <InlineAlert severity="warning">
          The last capability check didn’t complete. You can still run modeling.{' '}
          <Link to={keySettingsHref}>Re-check capability</Link>
        </InlineAlert>
      </div>
    );
  }
  // done + a verdict
  const severity = run.status === 'verified' ? 'info' : 'warning';
  return (
    <div className={styles.capabilityBanner}>
      <InlineAlert severity={severity}>
        <span className={styles.capabilityHeadline}>
          <StatusBadge kind="capability" status={run.status} /> model capability
        </span>{' '}
        {verdictReason(run.status, run.reason)}{' '}
        <Link to={keySettingsHref}>Model &amp; key settings</Link>
      </InlineAlert>
    </div>
  );
}

/** Honest, user-facing copy for a modeling failure reason. Unknown reasons fall
 * back to the raw reason so nothing is hidden. A provider content-filter is a
 * provider REFUSAL, not a model producing garbage — say so. */
function describeModelingFailure(reason: string): string {
  switch (reason) {
    case 'content_filtered':
      return 'The AI provider’s safety filter declined to model this journey — this can happen with sign-in or credential flows.';
    case 'invalid_key':
      return 'The AI provider rejected the API key. Check your key in settings and try again.';
    case 'provider_unreachable':
      return 'Couldn’t reach the AI provider. Check the key’s base URL and try again.';
    case 'inference_timeout':
      return 'The AI provider took too long to respond. Try again.';
    case 'model_unavailable':
      return 'The configured model isn’t available for this key.';
    case 'workspace_id_required':
      return 'This key needs an Anthropic workspace ID configured in settings.';
    case 'rate_limited':
      return 'The AI provider rate-limited the request. Wait a moment and try again.';
    case 'malformed_output':
      return 'The model’s output couldn’t be parsed into a journey model. Try again.';
    default:
      return `Modeling didn’t produce a revision: ${reason}`;
  }
}

/** Modeling-failure reasons where the MODEL itself (not the key/transport/rate) is
 * the likely culprit — so we route the user toward a more reliable model (#271). */
const MODEL_ATTRIBUTABLE_REASONS = new Set(['content_filtered', 'malformed_output', 'model_unavailable']);

/** Guidance shown after a model-attributable failure (#271): name the active model
 * and, when a capability verdict exists for THAT model, say it's below the floor,
 * then link to the model settings so the user can switch to a model that passes the
 * capability check. Reuses the already-loaded org-wide capability verdict. */
function ModelSwitchGuidance({
  model,
  capRun,
  keySettingsHref,
}: {
  model: string | null;
  capRun: CapabilityRunView | null;
  keySettingsHref: string;
}) {
  // Only trust the verdict if it is for the SAME model that just failed.
  const verdict =
    capRun && capRun.state === 'done' && capRun.model === model ? capRun.status : null;
  const belowFloorLabel =
    verdict === 'limited' || verdict === 'inconclusive' ? VERDICT_SUMMARY[verdict] : null;
  const verdictClause = belowFloorLabel
    ? ` is marked ${belowFloorLabel} for journey modeling.`
    : ' may not be the best fit here.';
  return (
    <>
      {' '}
      {model ? (
        <>
          Your model <strong>{model}</strong>
          {verdictClause}{' '}
        </>
      ) : null}
      A model that passes the capability check tends to model journeys more reliably.{' '}
      <Link to={keySettingsHref}>Change model in settings</Link>
    </>
  );
}

/** Live-region outcome of a modeling run (#229). InlineAlert supplies the live
 * role (status/alert) — do not double-wrap. Nothing here is ever "verified". */
function RunOutcome({
  runState,
  outcome,
  message,
  needsKey,
  raced,
  keySettingsHref,
  model,
  capRun,
}: {
  runState: RunState;
  outcome: JourneyModelingOutcome | null;
  message: string | null;
  needsKey: boolean;
  raced: boolean;
  keySettingsHref: string;
  model: string | null;
  capRun: CapabilityRunView | null;
}) {
  // A SINGLE, always-mounted live region (InlineAlert stays mounted when empty) so
  // the outcome is reliably announced: the region pre-exists before its content
  // changes, and it isn't newly inserted into #root as the dialog removes `inert`.
  let severity: AlertSeverity = 'info';
  let content: ReactNode = null;
  if (runState === 'checking-key') {
    content = 'Checking your key…';
  } else if (runState === 'running') {
    content = 'Running AI modeling…';
  } else if (runState === 'failed') {
    severity = 'error';
    if (needsKey) {
      content = (
        <>
          Couldn’t start modeling — your AI key is missing or revoked.{' '}
          <Link to={keySettingsHref}>Reconfigure your key</Link>
        </>
      );
    } else if (raced) {
      content = (
        <>
          Couldn’t start modeling — your key may have changed or this journey is no longer
          available. <Link to={keySettingsHref}>Check key settings</Link> and refresh.
        </>
      );
    } else {
      content = message ?? 'Something went wrong. Please try again.';
    }
  } else if (runState === 'done' && outcome) {
    if (outcome.status === 'failed') {
      severity = 'warning';
      content = (
        <>
          {describeModelingFailure(outcome.reason)}
          {MODEL_ATTRIBUTABLE_REASONS.has(outcome.reason) ? (
            <ModelSwitchGuidance model={model} capRun={capRun} keySettingsHref={keySettingsHref} />
          ) : null}
        </>
      );
    } else {
      severity = 'success';
      content = outcome.reused
        ? `Reused revision ${outcome.revisionNumber} — no charge (an identical model already existed).`
        : `Generated revision ${outcome.revisionNumber}. It’s an unverified draft — review it below.`;
    }
  }

  return (
    <div className={styles.runOutcome}>
      <InlineAlert severity={severity}>{content}</InlineAlert>
    </div>
  );
}

function RevisionDetailSection({
  phase,
  revision,
  error,
}: {
  phase: SectionState;
  revision: MarkedRevisionView | null;
  error: string | null;
}) {
  return (
    <section className={styles.section} aria-labelledby="jd-revision-detail-heading">
      <h2 id="jd-revision-detail-heading" className={styles.sectionHeading}>
        Revision detail
      </h2>
      {phase === 'loading' ? (
        <div className={styles.loading} role="status">
          <Spinner />
          <span>Loading revision…</span>
        </div>
      ) : null}
      {phase === 'error' ? <InlineAlert severity="error">{error}</InlineAlert> : null}
      {phase === 'ready' && revision ? <RevisionBody revision={revision} /> : null}
    </section>
  );
}

function RevisionBody({ revision }: { revision: MarkedRevisionView }) {
  return (
    <div className={styles.revisionDetail}>
      <p className={styles.revisionDetailMeta}>
        Revision {revision.revisionNumber} · {revision.model} · {revision.generatorVersion}
        {formatTime(revision.createdAt) ? ` · ${formatTime(revision.createdAt)}` : ''}
      </p>

      <dl className={styles.markerGrid}>
        <div className={styles.markerRow}>
          <dt>Confidence threshold</dt>
          <dd>{revision.appliedThreshold === null ? 'Not applied' : revision.appliedThreshold}</dd>
        </div>
        <div className={styles.markerRow}>
          <dt>Fail-closed</dt>
          <dd>{revision.failClosed ? 'Yes' : 'No'}</dd>
        </div>
        <div className={styles.markerRow}>
          <dt>Actual cost</dt>
          <dd>{costLabel(revision.actualMicroUsd)}</dd>
        </div>
        {revision.totalTokens !== null ? (
          <div className={styles.markerRow}>
            <dt>Tokens</dt>
            <dd>
              {revision.totalTokens} total
              {revision.promptTokens !== null && revision.completionTokens !== null
                ? ` (${revision.promptTokens} in · ${revision.completionTokens} out)`
                : ''}
            </dd>
          </div>
        ) : null}
      </dl>

      {revision.failClosed ? (
        <InlineAlert severity="warning">
          This revision was marked fail-closed: its confidence configuration was invalid, so every
          step is treated as tentative.
        </InlineAlert>
      ) : null}

      {revision.preconditions.length > 0 ? (
        <div className={styles.subsection}>
          <h3 className={styles.subHeading}>Preconditions</h3>
          <ul className={styles.plainList}>
            {revision.preconditions.map((pre, i) => (
              <li key={`pre-${i}`}>{pre}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {revision.dataRequirements.length > 0 ? (
        <div className={styles.subsection}>
          <h3 className={styles.subHeading}>Data requirements</h3>
          <ul className={styles.plainList}>
            {revision.dataRequirements.map((req, i) => (
              <li key={`data-${i}`}>
                {req.name} <span className={styles.stepMeta}>({req.kind})</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.subsection}>
        <h3 className={styles.subHeading}>Modeled steps</h3>
        <ol className={styles.stepList}>
          {revision.steps.map((step) => (
            <li key={step.stepOrder} className={styles.step}>
              <span className={styles.stepOrder}>{step.stepOrder}</span>
              <span className={styles.stepLabel}>{step.intent}</span>
              <span className={styles.mutedBadge}>{step.actionKind}</span>
              <span className={styles.mutedBadge}>
                {step.provenance.kind === 'observed' ? 'observed' : 'inferred'}
              </span>
              <span className={styles.mutedBadge}>
                {step.tentative ? 'tentative' : 'not tentative'}
              </span>
              {step.targetHint ? <span className={styles.stepMeta}>{step.targetHint}</span> : null}
            </li>
          ))}
        </ol>
      </div>

      {revision.expectedResults.length > 0 ? (
        <div className={styles.subsection}>
          <h3 className={styles.subHeading}>Expected results</h3>
          <ul className={styles.resultList}>
            {revision.expectedResults.map((res) => (
              <li key={`result-${res.stepOrder}`} className={styles.result}>
                <span className={styles.stepOrder}>{res.stepOrder}</span>
                <span className={styles.stepLabel}>{res.description}</span>
                <span className={styles.unverifiedBadge}>Unverified</span>
                {res.tentative ? <span className={styles.mutedBadge}>tentative</span> : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
